import type React from 'react';
import type { CostpointTag, CostpointTransaction } from '../../types/reporting.types';
import { formatEuro } from '../../utils/currency';

interface CostpointDetailViewProps {
  selectedCostpoint: string;
  costpointTags: CostpointTag[];
  costpointTransactions: CostpointTransaction[];
  isLoadingCostpointData: boolean;
  selectedTagForTransactions: string;
  onTagSelect: (tagName: string) => void;
}

const CostpointDetailView: React.FC<CostpointDetailViewProps> = ({
  selectedCostpoint,
  costpointTags,
  costpointTransactions,
  isLoadingCostpointData,
  selectedTagForTransactions,
  onTagSelect,
}) => {
  return (
    <div className="mt-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {selectedCostpoint.toUpperCase()} - All Tags & Transactions
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Complete overview of all tags and transactions for {selectedCostpoint.toUpperCase()}{' '}
            costpoint
          </p>
        </div>

        <div className="p-6">
          {isLoadingCostpointData ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-gray-500">Loading costpoint data...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Tags Overview */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4">
                  Tags Overview ({costpointTags.length} tags)
                </h3>
                {costpointTags.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {costpointTags.map((tag) => (
                      <div
                        key={tag.id}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">{tag.display_name}</h4>
                          <span className="text-sm text-gray-500">
                            {tag.transaction_count} transactions
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total:</span>
                            <span className="font-medium">{formatEuro(tag.total_amount)}</span>
                          </div>
                          {tag.income_amount > 0 && (
                            <div className="flex justify-between">
                              <span className="text-green-600">Income:</span>
                              <span className="text-green-600 font-medium">
                                {formatEuro(tag.income_amount)}
                              </span>
                            </div>
                          )}
                          {tag.expense_amount > 0 && (
                            <div className="flex justify-between">
                              <span className="text-red-600">Expenses:</span>
                              <span className="text-red-600 font-medium">
                                {formatEuro(tag.expense_amount)}
                              </span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() =>
                            onTagSelect(selectedTagForTransactions === tag.name ? '' : tag.name)
                          }
                          className={`mt-3 w-full px-3 py-1 rounded text-sm font-medium transition-colors ${
                            selectedTagForTransactions === tag.name
                              ? 'bg-indigo-600 text-white'
                              : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                          }`}
                        >
                          {selectedTagForTransactions === tag.name
                            ? 'Hide Transactions'
                            : 'View Transactions'}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <p>No tags found for this costpoint in the selected period</p>
                  </div>
                )}
              </div>

              {/* Transactions Table */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4">
                  Transactions
                  {selectedTagForTransactions && (
                    <span className="text-sm text-gray-600 ml-2">
                      (filtered by "{selectedTagForTransactions}")
                    </span>
                  )}
                  <span className="text-sm text-gray-500 ml-2">
                    ({costpointTransactions.length} transactions)
                  </span>
                </h3>
                {costpointTransactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tag
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Allocated (Net)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {costpointTransactions.map((transaction) => (
                          <tr key={transaction.transaction_id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(transaction.date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              <div className="max-w-xs truncate">{transaction.description}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {transaction.tag_display_name || '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  transaction.type === 'deposit'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {transaction.type}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatEuro(transaction.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatEuro(transaction.allocated_amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <p>
                      {selectedTagForTransactions
                        ? `No transactions found for tag "${selectedTagForTransactions}"`
                        : 'No transactions found for this costpoint in the selected period'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostpointDetailView;
